<template>
  <!-- When one schedule applies: dates (optional), days of the week and time of day, with a
       one-line summary. Shared by the price, promo code and choice schedule modals. Changes go
       out as `update` patches; the modal merges them into its own row. -->
  <div class="sw">
    <div class="sw-row">
      <div class="sw-label">Dates</div>
      <div class="sw-dates">
        <div class="sw-datetime">
          <Datepicker
            :modelValue="toPickerDate(schedule.start_at)"
            @update:modelValue="setDate('start_at', $event)"
            format="dd/MM/yyyy"
            :enable-time-picker="false"
            auto-apply
            placeholder="No start date"
            class="sw-date"
          />
          <Datepicker
            :modelValue="toPickerTime(schedule.start_at)"
            @update:modelValue="setDateTime('start_at', $event)"
            timePicker
            :clearable="false"
            :disabled="!schedule.start_at"
            placeholder="Time"
            class="sw-date sw-time"
          />
        </div>
        <span class="sw-arrow">→</span>
        <div class="sw-datetime">
          <Datepicker
            :modelValue="toPickerDate(schedule.end_at)"
            @update:modelValue="setDate('end_at', $event)"
            format="dd/MM/yyyy"
            :enable-time-picker="false"
            auto-apply
            :min-date="toPickerDate(schedule.start_at)"
            placeholder="No end date"
            class="sw-date"
          />
          <Datepicker
            :modelValue="toPickerTime(schedule.end_at)"
            @update:modelValue="setDateTime('end_at', $event)"
            timePicker
            :clearable="false"
            :disabled="!schedule.end_at"
            placeholder="Time"
            class="sw-date sw-time"
          />
        </div>
      </div>
    </div>

    <div class="sw-row">
      <div class="sw-label">Days</div>
      <div>
        <div class="sw-presets">
          <button
            v-for="preset in presets"
            :key="preset.label"
            type="button"
            class="sw-preset"
            :class="{ 'sw-preset--on': isPreset(schedule.days, preset.days) }"
            @click="$emit('update', { days: [...preset.days] })"
          >
            {{ preset.label }}
          </button>
        </div>
        <div class="sw-days">
          <button
            v-for="day in days"
            :key="day.value"
            type="button"
            class="sw-day"
            :class="{ 'sw-day--on': schedule.days.includes(day.value) }"
            :aria-pressed="schedule.days.includes(day.value)"
            @click="toggleDay(day.value)"
          >
            {{ day.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="sw-row">
      <div class="sw-label">Time</div>
      <div class="sw-time-row">
        <div class="sw-mode">
          <label class="sw-radio">
            <input
              type="radio"
              :name="idPrefix + '_time_mode'"
              :checked="!hasWindow"
              @change="$emit('update', { time_from: '', time_to: '' })"
            />
            All day
          </label>
          <label class="sw-radio">
            <input
              type="radio"
              :name="idPrefix + '_time_mode'"
              :checked="hasWindow"
              @change="$emit('update', { time_from: '09:00', time_to: '17:00' })"
            />
            Set hours
          </label>
        </div>
        <div v-if="hasWindow" class="sw-hours">
          <Datepicker
            :modelValue="toTime(schedule.time_from)"
            @update:modelValue="setTime('time_from', $event)"
            timePicker
            :clearable="false"
            placeholder="From"
            class="sw-date sw-time"
          />
          <span class="sw-arrow">–</span>
          <Datepicker
            :modelValue="toTime(schedule.time_to)"
            @update:modelValue="setTime('time_to', $event)"
            timePicker
            :clearable="false"
            placeholder="To"
            class="sw-date sw-time"
          />
          <span v-if="overnight" class="sw-next-day">ends next day</span>
        </div>
      </div>
    </div>

    <div class="sw-summary">
      <i class="material-icons sw-summary__icon">event_repeat</i>
      <span>{{ summary }}</span>
    </div>
  </div>
</template>

<script>
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import moment from "moment";
import {
  PRICE_SCHEDULE_FORMAT,
  SCHEDULE_DAYS,
  isOvernight,
  isPreset,
  scheduleDayPresets,
  scheduleWhenSummary,
} from "@/config/priceSchedule";

const pad = (number) => String(number).padStart(2, "0");

export default {
  name: "ScheduleWhenFields",
  components: { Datepicker },
  props: {
    // { start_at, end_at, days, time_from, time_to } (config/priceSchedule.js)
    schedule: { type: Object, required: true },
    // Unique per row, for the radio group name.
    idPrefix: { type: String, required: true },
  },
  emits: ["update"],
  data() {
    return { days: SCHEDULE_DAYS, presets: scheduleDayPresets };
  },
  computed: {
    hasWindow() {
      return !!(this.schedule.time_from || this.schedule.time_to);
    },
    overnight() {
      return isOvernight(this.schedule);
    },
    summary() {
      return scheduleWhenSummary(this.schedule);
    },
  },
  methods: {
    isPreset,
    toggleDay(day) {
      const days = this.schedule.days.includes(day)
        ? this.schedule.days.filter((value) => value !== day)
        : [...this.schedule.days, day].sort((a, b) => a - b);
      this.$emit("update", { days });
    },
    // Dates are kept as "YYYY-MM-DD HH:mm", Singapore wall-clock time on both sides, so the
    // browser's own time zone does not shift them.
    toPickerDate(value) {
      return value ? moment(value, PRICE_SCHEDULE_FORMAT).toDate() : null;
    },
    toPickerTime(value) {
      return value ? this.toTime(value.slice(11, 16)) : null;
    },
    toTime(value) {
      return value ? { hours: Number(value.slice(0, 2)), minutes: Number(value.slice(3, 5)) } : null;
    },
    // A date alone starts at 00:00 or ends at 23:59, so one click covers whole days. Clearing
    // it removes that limit.
    setDate(field, date) {
      if (!date) {
        this.$emit("update", { [field]: "" });
        return;
      }
      const time = this.schedule[field] ? this.schedule[field].slice(11, 16) : field === "start_at" ? "00:00" : "23:59";
      this.emitDates({ [field]: `${moment(date).format("YYYY-MM-DD")} ${time}` });
    },
    setDateTime(field, time) {
      if (!time || !this.schedule[field]) {
        return;
      }
      this.emitDates({ [field]: `${this.schedule[field].slice(0, 10)} ${pad(time.hours)}:${pad(time.minutes)}` });
    },
    // A new start pushes an end that is no longer later to the end of that day (or an hour on).
    emitDates(patch) {
      const start = patch.start_at ?? this.schedule.start_at;
      const end = patch.end_at ?? this.schedule.end_at;
      if (patch.start_at && end && end <= start) {
        const endOfDay = `${start.slice(0, 10)} 23:59`;
        patch.end_at = endOfDay > start ? endOfDay : moment(start, PRICE_SCHEDULE_FORMAT).add(1, "hour").format(PRICE_SCHEDULE_FORMAT);
      }
      this.$emit("update", patch);
    },
    setTime(field, time) {
      if (!time) {
        return;
      }
      this.$emit("update", { [field]: `${pad(time.hours)}:${pad(time.minutes)}` });
    },
  },
};
</script>

<style scoped>
.sw {
  display: grid;
  gap: 12px;
}
.sw-row {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}
.sw-label {
  padding-top: 9px;
  font-size: 12px;
  font-weight: 600;
  color: #7b809a;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.sw-dates {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.sw-datetime {
  display: grid;
  grid-template-columns: 150px 110px;
  gap: 6px;
}
.sw-arrow {
  color: #9aa0ac;
}
.sw-date :deep(.dp__input) {
  font-size: 14px;
  color: #344767;
  height: 38px;
}
.sw-presets,
.sw-days {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sw-presets {
  margin-bottom: 6px;
}
.sw-preset {
  border: 0;
  background: none;
  padding: 2px 4px;
  font-size: 12px;
  color: #7b809a;
  text-decoration: underline dotted;
  cursor: pointer;
}
.sw-preset--on {
  color: #344767;
  font-weight: 700;
  text-decoration: none;
}
.sw-day {
  min-width: 46px;
  height: 34px;
  border: 1px solid #d2d6da;
  border-radius: 17px;
  background: #fff;
  color: #67748e;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.sw-day--on {
  border-color: #344767;
  background: #344767;
  color: #fff;
}
.sw-time-row {
  display: grid;
  gap: 8px;
}
.sw-mode {
  display: flex;
  gap: 18px;
  padding-top: 8px;
}
.sw-radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  color: #344767;
  cursor: pointer;
}
.sw-hours {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.sw-hours .sw-time {
  width: 120px;
}
.sw-next-day {
  font-size: 12px;
  font-weight: 600;
  color: #b26a00;
  background: #fff3e0;
  border-radius: 10px;
  padding: 2px 8px;
}
.sw-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #344767;
  background: #f4f6f9;
  border-radius: 8px;
  padding: 8px 10px;
}
.sw-summary__icon {
  font-size: 16px;
  color: #7b809a;
}
@media (max-width: 767px) {
  .sw-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .sw-label {
    padding-top: 0;
  }
  .sw-datetime {
    grid-template-columns: minmax(0, 1fr) 100px;
    width: 100%;
  }
  .sw-arrow {
    display: none;
  }
}
</style>
